import { useEffect, useMemo, useRef, useState } from "react";
import { releaseMic } from "@/lib/recorder";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Shuffle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PARTS, partOf, TIERS, tierRate } from "@/data/parts";
import { getBank, getDrill, saveDrill, clearDrill } from "@/lib/bank";
import { scoreItem } from "@/lib/scoring";
import { recordActivity, hasAnswer } from "@/lib/streak";
import { ItemRenderer } from "@/components/items/ItemRenderer";
import { Feedback } from "@/components/Feedback";
import { DrillBanner, DrillSummary } from "@/components/WeakSpot";
import { QuestionJump, QuestionPager, QuestionGridSheet, ResumeDialog } from "@/components/PracticeNav";
import { getProgress, progressKey, setLast, recordScore } from "@/lib/progress";
import { useAuth } from "@/context/AuthContext";
import { LeadCaptureModal, UpgradeLock } from "@/components/AccessGates";
import { DEMO_LIMITS } from "@/lib/bank";
import { api } from "@/lib/api";
import { HardwareGate, useHardwareGate } from "@/components/HardwareGate";

const SPEEDS = [0.8, 1.0, 1.2];

// Free demo needs lead details (or an account) before any sample loads
export default function PracticeGate() {
  const { ready, canDemo } = useAuth();
  const nav = useNavigate();
  if (!ready) return null;
  if (!canDemo) return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
      <p data-testid="practice-demo-required" className="text-slate-400">Enter your details to start the free demo.</p>
      <LeadCaptureModal open onOpenChange={(o) => { if (!o && !localStorage.getItem("lexv_guest")) nav("/"); }} />
    </div>
  );
  return <HardwareGate><Practice /></HardwareGate>;
}

const triedInPart = (part, bank) => {
  const ids = new Set(bank[part].map((x) => x.id));
  return new Set(["easy", "medium", "hard"].flatMap((t) => Object.keys(getProgress(progressKey(part, t)).scores)).filter((id) => ids.has(id))).size;
};

function Practice() {
  const { hasAccess, bankRev, guest } = useAuth();
  const bank = useMemo(() => getBank(), [bankRev]); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = useNavigate();
  const { ensure, ready: hwReady } = useHardwareGate();
  const [params] = useSearchParams();
  const [drill, setDrill] = useState(() => (params.get("drill") ? getDrill() : null));
  const [partKey, setPartKey] = useState(() => (PARTS.some((p) => p.key === params.get("part")) ? params.get("part") : "A"));
  const [idx, setIdx] = useState(0);
  const [rate, setRate] = useState(1.0);
  const [attempt, setAttempt] = useState(0);
  const [done, setDone] = useState(null);
  const attemptRef = useRef(0);
  attemptRef.current = attempt;
  useEffect(() => () => releaseMic(), []);
  const dItem = drill && drill.idx < drill.queue.length ? drill.queue[drill.idx] : null;
  const activeKey = dItem ? dItem.part : partKey;
  const part = partOf(activeKey);
  const [tier, setTier] = useState("easy");
  const items = bank[partKey].filter((x) => (x.difficulty || "medium") === tier);
  const item = dItem ? dItem.item : items[idx % items.length];
  const tried = hasAccess ? 0 : triedInPart(partKey, bank);
  const demoDone = !hasAccess && !drill && tried >= DEMO_LIMITS[partKey];
  // First time a section's free samples run out: ask the server to send one friendly upgrade email
  useEffect(() => {
    if (!demoDone || localStorage.getItem("lexv_followup_sent")) return;
    localStorage.setItem("lexv_followup_sent", partKey);
    const g = !localStorage.getItem("lexv_token") && guest?.token;
    api.post("/leads/preview-complete", { part: partKey }, g ? { headers: { Authorization: `Bearer ${g}` } } : undefined).catch(() => localStorage.removeItem("lexv_followup_sent"));
  }, [demoDone, partKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const pk = progressKey(partKey, tier);
  const [prog, setProg] = useState(() => getProgress(pk));
  const [gridOpen, setGridOpen] = useState(false);
  const [resume, setResume] = useState(null);
  const idxRef = useRef(0);
  idxRef.current = idx;
  useEffect(() => {
    setProg(getProgress(pk));
    // After a sign-in sync, offer to resume if still sitting on Question 1
    const h = () => { const p = getProgress(pk); setProg(p); if (p.last > 0 && idxRef.current === 0 && !getDrill()) setResume((r) => r || { key: pk, last: p.last, label: `Part ${partKey} · ${TIERS.find((x) => x.key === tier)?.label}` }); };
    window.addEventListener("lexv-progress", h); return () => window.removeEventListener("lexv-progress", h); }, [pk]); // eslint-disable-line react-hooks/exhaustive-deps
  const reset = () => { setDone(null); setAttempt((a) => a + 1); };
  const go = (i) => { const n = Math.max(0, Math.min(items.length - 1, i)); setIdx(n); reset(); setProg(setLast(pk, n)); };
  const jumpTo = (t, i) => { setTier(t); setIdx(i); reset(); setProg(setLast(progressKey(partKey, t), i)); };
  // Entering a module/level with saved progress asks to resume
  const enter = (k, t) => {
    ensure();
    const key = progressKey(k, t), p = getProgress(key);
    setIdx(0); reset();
    if (p.last > 0) setResume({ key, last: p.last, label: `Part ${k} · ${TIERS.find((x) => x.key === t)?.label}` });
  };
  useEffect(() => { if (!drill) enter(partKey, tier); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const choosePart = (k) => { setPartKey(k); enter(k, tier); };
  const updDrill = (d) => { saveDrill(d); setDrill(d); };
  const nextItem = () => { if (drill) { updDrill({ ...drill, idx: drill.idx + 1 }); setDone(null); setAttempt((a) => a + 1); } else go(idx + 1 >= items.length ? 0 : idx + 1); };
  const exitDrill = () => { clearDrill(); setDrill(null); setDone(null); nav("/practice", { replace: true }); };
  const onSubmit = async (raw) => {
    const token = attemptRef.current;
    const k = activeKey, it = item, cur = drill ? getDrill() || drill : null;
    if (raw.pending && cur?.timed) {
      const di = cur.idx, id = cur.id;
      raw.pending.then((t) => {
        const d = getDrill();
        if (!d || d.id !== id) return;
        const resp = { ...d.responses[di], text: t };
        d.responses[di] = resp; d.scores[di] = scoreItem(k, it, resp).score;
        saveDrill(d); setDrill({ ...d });
        if (t.trim()) recordActivity();
      });
    } else if (raw.pending) {
      raw = { ...raw, text: await raw.pending };
      if (token !== attemptRef.current) return;
    }
    const { pending, ...response } = raw;
    if (hasAnswer(response)) recordActivity();
    const result = scoreItem(k, it, response);
    if (cur) {
      const scores = [...cur.scores], responses = [...(cur.responses || [])];
      scores[cur.idx] = result.score; responses[cur.idx] = response;
      if (cur.timed) { updDrill({ ...cur, scores, responses, idx: cur.idx + 1 }); setAttempt((a) => a + 1); return; }
      updDrill({ ...cur, scores, responses });
    } else setProg(recordScore(pk, idx, it.id, result.score));
    setDone({ response, result });
  };
  const timed = !!drill?.timed;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-8">
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-indigo-300">Practice mode</span>
      <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Targeted Practice Arena</h1>
      <p className="mt-2 text-slate-400">{drill?.timed ? "Timed drill under exam conditions: audio plays once, no replay, and timers auto-advance." : "Pick a module, replay audio at any speed, and see the correct answer with a word-by-word diff instantly."}</p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        {drill && <DrillBanner drill={drill} onExit={exitDrill} />}
        {!drill && (
          <div className="flex rounded-full bg-slate-800 p-1" data-testid="practice-tier-tabs">
            {TIERS.map((t) => <button key={t.key} data-testid={`practice-tier-${t.key}`} onClick={() => { setTier(t.key); enter(partKey, t.key); }} className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${tier === t.key ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}>{t.label} <span className="font-mono opacity-60">{bank[partKey].filter((x) => (x.difficulty || "medium") === t.key).length}</span></button>)}
          </div>
        )}
        {!drill && <Select value={partKey} onValueChange={choosePart}>
          <SelectTrigger data-testid="practice-module-select" className="w-full border-slate-700 bg-slate-800 text-white sm:w-72"><SelectValue /></SelectTrigger>
          <SelectContent>{PARTS.map((p) => <SelectItem key={p.key} value={p.key} data-testid={`practice-module-option-${p.key}`}>Part {p.key} · {p.name} ({bank[p.key].length})</SelectItem>)}</SelectContent>
        </Select>}
        {part.audio && !timed && !(drill && !dItem) && (
          <div className="flex rounded-full bg-slate-800 p-1" data-testid="practice-speed-toggle">
            {SPEEDS.map((s) => <button key={s} data-testid={`speed-${s}`} onClick={() => setRate(s)} className={`rounded-full px-3 py-1 font-mono text-xs font-semibold transition-colors ${rate === s ? "bg-indigo-600 text-white" : "text-slate-300 hover:text-white"}`}>{s.toFixed(1)}x</button>)}
          </div>
        )}
        {!drill && <div className="flex w-full items-center justify-between gap-2 sm:ml-auto sm:w-auto">
          <QuestionJump idx={idx} total={items.length} onGo={go} onOpenGrid={() => setGridOpen(true)} />
          <button data-testid="practice-random-btn" onClick={() => go(Math.floor(Math.random() * items.length))} className="rounded-full p-2 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white" aria-label="Random question"><Shuffle className="h-5 w-5" /></button>
          <span data-testid="practice-item-counter" className="sr-only">{item.id} · {idx + 1}/{items.length}</span>
        </div>}
      </div>
      {!(drill && !dItem) && <p className="mt-4 text-sm text-slate-400">{dItem && <b className="mr-1 text-indigo-300">Part {part.key} · {part.name}:</b>}{part.instr}</p>}

      {!hasAccess && !drill && (
        <div data-testid="demo-banner" className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-100">
          <span className="font-semibold">Free preview</span>
          <span data-testid="demo-samples-used" className="font-mono text-xs">{Math.min(tried, DEMO_LIMITS[partKey])} / {DEMO_LIMITS[partKey]} samples used in Part {partKey}</span>
          <span className="h-1.5 min-w-[80px] flex-1 overflow-hidden rounded-full bg-emerald-950/50"><span className="block h-full rounded-full bg-emerald-400 transition-[width] duration-500" style={{ width: `${Math.min(100, (tried / DEMO_LIMITS[partKey]) * 100)}%` }} /></span>
          <Link to="/pricing" data-testid="demo-banner-upgrade-link" className="font-semibold text-white underline-offset-2 hover:underline">Unlock everything</Link>
        </div>
      )}
      <div className="mt-4 box-border w-full min-w-0 max-w-full rounded-xl border border-slate-200 bg-white p-4 shadow-xl shadow-slate-950/40 sm:p-6 lg:p-10">
        {drill && !dItem ? (
          <DrillSummary drill={drill} onFinish={exitDrill} />
        ) : demoDone && !done ? (
          <UpgradeLock testId="practice-demo-lock" />
        ) : !hwReady ? (
          <div data-testid="practice-paused-for-mic" className="grid min-h-[12rem] place-items-center text-sm text-slate-600">Paused until your microphone is enabled…</div>
        ) : resume ? (
          <div data-testid="practice-waiting-resume" className="grid min-h-[12rem] place-items-center text-sm text-slate-600">Choose where to continue…</div>
        ) : done ? (
          <div className="space-y-6">
            <Feedback partKey={activeKey} item={item} response={done.response} result={done.result} rate={tierRate(item.difficulty || tier) * rate} autoAi />
            <div className="flex flex-wrap gap-2">
              <button data-testid="practice-retry-btn" onClick={() => { setDone(null); setAttempt((a) => a + 1); }} className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100">Try again</button>
              <button data-testid="practice-next-item-btn" onClick={nextItem} autoFocus className="rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700">{drill && drill.idx + 1 >= drill.queue.length ? "See drill results" : "Next item"}</button>
            </div>
          </div>
        ) : (
          <ItemRenderer key={`${item.id}-${attempt}`} part={part} item={item} mode={timed ? "exam" : "practice"} rate={tierRate(item.difficulty || tier) * (timed ? 1 : rate)} onSubmit={onSubmit} />
        )}
      </div>
      {!drill && <QuestionPager idx={idx} total={items.length} items={items} progress={prog} onGo={go} />}
      {!drill && <QuestionGridSheet open={gridOpen} onOpenChange={setGridOpen} bank={bank} partKey={partKey} partName={part.name} tier={tier} idx={idx} onJump={jumpTo} />}
      <ResumeDialog resume={hwReady ? resume : null} items={items}
        onResume={() => { const n = Math.min(resume.last, items.length - 1); setIdx(n); reset(); setResume(null); }}
        onRestart={() => { setIdx(0); reset(); setProg(setLast(resume.key, 0)); setResume(null); }} />
    </div>
  );
}
